import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import type { SharePointConnectionHealth } from '@/services/api_client';
import apiClient from '@/services/api_client';
import { useTenantStore } from '@/stores/tenant';

export const INVALID_SECRET_ADMIN_HINT =
    "The Microsoft app registration's client secret is invalid or expired. Ask an administrator to update it in Settings > Connections.";

/**
 * Whether the app registration's own access (its client secret) still works. SharePoint and
 * Azure AD groups depend on it; Outlook and Teams use each user's sign-in and do not.
 * Shared so every screen that warns about it reads one Graph check, and a secret updated in
 * settings clears the warnings everywhere.
 */
export const useMicrosoftConnectionStore = defineStore(
    'microsoftConnection',
    () => {
        const tenantStore = useTenantStore();
        const health = ref<SharePointConnectionHealth | null>(null);
        const isLoading = ref(false);
        let isRequested = false;
        let loadedTenantId: string | undefined;
        let requestId = 0;

        async function refresh() {
            const currentRequestId = ++requestId;
            const tenantId = tenantStore.tenantId;
            const isCurrent = () =>
                currentRequestId === requestId &&
                tenantStore.tenantId === tenantId;

            if (tenantId !== loadedTenantId) health.value = null;
            loadedTenantId = tenantId;
            if (!tenantId) {
                isLoading.value = false;
                return;
            }

            isLoading.value = true;
            try {
                const result =
                    await apiClient.getSharePointConnectionHealth(tenantId);
                if (isCurrent()) health.value = result;
            } catch (error) {
                console.error(
                    'Could not check the Microsoft connection:',
                    error
                );
                if (isCurrent()) {
                    health.value = {
                        status: 'unavailable',
                        detail: 'SharePoint access could not be verified. Please try again.'
                    };
                }
            } finally {
                if (isCurrent()) isLoading.value = false;
            }
        }

        // Every screen that opens re-checks, so a secret fixed elsewhere clears its warning;
        // notices mounting together share the check already in flight.
        function ensureLoaded() {
            isRequested = true;
            if (isLoading.value && loadedTenantId === tenantStore.tenantId)
                return;
            void refresh();
        }

        watch(
            () => tenantStore.tenantId,
            () => {
                if (isRequested) void refresh();
            }
        );

        return { health, isLoading, refresh, ensureLoaded };
    }
);
